import './Confirm.module.less';

import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { clsx } from 'clsx';
import { type FC, type PropsWithChildren } from 'react';

import { Modal } from '../Modal';
import { SectionBar } from '../SectionBar';

export interface Props {
  title?: string;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  submitLabel?: string;
  submitDisabled?: boolean;
  cancelLabel?: string;
  maxWidth?: number | 'none';
  disabled?: boolean;
  /** Passed to `SectionBar` (e.g. header styling). */
  className?: string;
  /** Merged with the default `Confirm` class on the content wrapper `Typography`. */
  contentClassName?: string;
  /** Merged with default content styles; use to override padding (e.g. `{ p: 2 }` for 16px). */
  contentSx?: SxProps<Theme>;
  /** When true, content area uses dense padding (`theme.spacing(2)` = 16px by default). */
  denseContent?: boolean;
  onlyCloseOnButton?: boolean;
}

export const Confirm: FC<Props & PropsWithChildren> = ({
  open,
  title,
  onClose,
  onConfirm,
  submitLabel = 'Confirm',
  submitDisabled = false,
  cancelLabel = 'Cancel',
  maxWidth,
  children,
  disabled = false,
  className,
  contentClassName,
  contentSx,
  denseContent = false,
  onlyCloseOnButton = false,
}) => {
  const modalPaperMaxWidth: number | 'none' =
    maxWidth === 'none' ? 'none' : typeof maxWidth === 'number' ? maxWidth : 672;

  const mergedContentSx: SxProps<Theme> = [
    {
      flex: 1,
      minHeight: 0,
      maxHeight: 'min(72dvh, calc(100dvh - 12rem))',
      overflowX: 'hidden',
      overflowY: 'auto',
    },
    ...(denseContent ? [{ p: 2 }] : []),
    ...(contentSx == null ? [] : Array.isArray(contentSx) ? contentSx : [contentSx]),
  ];

  return (
    <Modal
      open={open}
      onClose={onlyCloseOnButton ? () => undefined : onClose}
      compact
      maxWidth={modalPaperMaxWidth}
      styles={{
        margin: '16px',
        maxHeight: 'calc(100dvh - 32px)',
        display: 'flex',
        flexDirection: 'column',
        width: modalPaperMaxWidth === 'none' ? 'auto' : 'min(100%, 100vw - 32px)',
      }}
    >
      <SectionBar
        title={title}
        className={className}
        actions={[
          {
            label: submitLabel,
            onClick: onConfirm,
            disabled: disabled || submitDisabled,
          },
          { label: cancelLabel, onClick: onClose, variant: 'outlined', disabled },
        ]}
        fixedActions
      />
      <Typography
        component="div"
        className={clsx('Confirm', contentClassName)}
        sx={mergedContentSx}
      >
        {children}
      </Typography>
    </Modal>
  );
};
